import * as THREE from "three";
import { IVfxTask } from "../../core/runtime/vfx_task_runner.js";
import { Vfx3DRuntime } from "../../vfx_3d_runtime.js";
import { IController, StopBehavior } from "../../core/types/controller_types.js";
import { VfxBallisticStreakBatch } from "../../core/batches/vfx_ballistic_streak_batch.js";
import { VfxImpactSmokeBatch } from "./vfx_impact_smoke_batch.js";
import { VfxPlanarWaveBatch } from "../../core/batches/vfx_planar_wave_batch.js";
import { VfxShardBatch } from "../../core/batches/vfx_shard_batch.js";
import { VfxSplashBatch } from "../../core/batches/vfx_splash_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { randomRange } from "../../../common/common_funcs.js";
import { VfxGradientAtlas } from "../../core/textures/vfx_gradient_atlas.js";

export enum BlockImpactMaterial
{
  STONE,
  WOOD,
  ICE,
  METAL,
  PLASTIC,
  BRICK,
  COLUMN,
  WATERMELON,
  GREEN_BOX,
  PURPLE_BOX,
  PLASTIC_BLUE,
  PLASTIC_GREEN,
  PLASTIC_LIGHT_BLUE,
  PLASTIC_ORANGE,
  PLASTIC_PINK,
  PLASTIC_PURPLE,
  PLASTIC_RED,
  PLASTIC_YELLOW
}

export class VfxBallBlockImpactTask implements IVfxTask, IController
{
  // --- Parabolic Sparks Constants (Metal Blocks) ---
  private static readonly SPARKS_BURST_COUNT         : number      = 6;
  private static readonly SPARKS_LIFESPAN            : number      = 0.5;
  private static readonly SPARKS_COLOR_1             : THREE.Color = new THREE.Color( 0xFFFFC8 );
  private static readonly SPARKS_COLOR_2             : THREE.Color = new THREE.Color( 0xFFB450 );
  private static readonly SPARKS_COLOR_3             : THREE.Color = new THREE.Color( 0xFF5014 );
  private static readonly SPARKS_COLOR_2_PROGRESS    : number      = 0.5;
  private static readonly SPARKS_COLOR_3_PROGRESS    : number      = 0.7;
  private static readonly SPARKS_MIN_SPEED           : number      = 3;
  private static readonly SPARKS_MAX_SPEED           : number      = 6;
  private static readonly SPARKS_UP_MIN              : number      = 2.0;
  private static readonly SPARKS_UP_MAX              : number      = 4.5;
  private static readonly SPARKS_GRAVITY             : number      = 22;
  private static readonly SPARKS_MIN_WIDTH           : number      = 0.54;
  private static readonly SPARKS_MAX_WIDTH           : number      = 0.6;
  private static readonly SPARKS_MIN_HEIGHT          : number      = 0.66;
  private static readonly SPARKS_MAX_HEIGHT          : number      = 0.72;
  private static readonly SPARKS_STRETCH_FACTOR      : number      = 0.05;
  private static readonly SPARKS_SHRINK_START_RATIO  : number      = 0.6;
  private static readonly SPARKS_SPAWN_AREA_RADIUS   : number      = 0.6;

  // --- Smoke Constants (Stone Blocks) ---
  private static readonly SMOKE_BURST_COUNT      : number      = 6;
  private static readonly SMOKE_LIFESPAN         : number      = 0.5;
  private static readonly SMOKE_MIN_FLIGHT_DIST  : number      = 0.5;
  private static readonly SMOKE_MAX_FLIGHT_DIST  : number      = 0.7;
  private static readonly SMOKE_MIN_START_SCALE  : number      = 0.2;
  private static readonly SMOKE_MAX_START_SCALE  : number      = 0.3;
  private static readonly SMOKE_MIN_END_SCALE    : number      = 0.6;
  private static readonly SMOKE_MAX_END_SCALE    : number      = 0.8;
  private static readonly SMOKE_COLOR            : THREE.Color = new THREE.Color( 0xFFFFDC );
  private static readonly SMOKE_INITIAL_ALPHA    : number      = 0.3;

  // --- Planar Shockwave Constants ---
  private static readonly WAVES_BURST_COUNT        : number      = 6;
  private static readonly WAVES_LIFESPAN           : number      = 0.2;
  private static readonly WAVES_SPEED              : number      = 2;
  private static readonly WAVES_MIN_SPAWN_AREA_RADIUS: number    = 0.25;
  private static readonly WAVES_MAX_SPAWN_AREA_RADIUS: number    = 0.45;
  private static readonly WAVES_MIN_WIDTH          : number      = 1.2;
  private static readonly WAVES_MAX_WIDTH          : number      = 1.8;
  private static readonly WAVES_MIN_HEIGHT         : number      = 0.8;
  private static readonly WAVES_MAX_HEIGHT         : number      = 1;
  private static readonly WAVES_FADE_START_PROGRESS: number      = 0.2;
  private static readonly WAVES_MAX_ALPHA          : number      = 0.3;
  private static readonly WAVES_COLOR              : THREE.Color = new THREE.Color( 0xFFFFFF );

  // --- Splinters Constants (Stone, Wood, Ice) ---
  private static readonly SPLINTERS_BURST_COUNT    : number      = 6;
  private static readonly SPLINTERS_LIFESPAN       : number      = 1;
  private static readonly SPLINTERS_MIN_SPEED      : number      = 2;
  private static readonly SPLINTERS_MAX_SPEED      : number      = 3;
  private static readonly SPLINTERS_GRAVITY        : number      = 20;
  private static readonly SPLINTERS_MIN_SCALE      : number      = 0.2;
  private static readonly SPLINTERS_MAX_SCALE      : number      = 0.4;
  private static readonly SPLINTERS_FADE_START     : number      = 0.6;
  private static readonly SPLINTERS_SHRINK_START   : number      = 1.0;
  
  private static readonly STONE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterStone1,
    ResourceID.SplinterStone2,
    ResourceID.SplinterStone3,
    ResourceID.SplinterStone4,
    ResourceID.SplinterStone5,
  ];

  private static readonly WOOD_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterWood1,
    ResourceID.SplinterWood2,
    ResourceID.SplinterWood3,
    ResourceID.SplinterWood4,
    ResourceID.SplinterWood5,
  ];

  private static readonly BRICK_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterBrick1,
    ResourceID.SplinterBrick2,
    ResourceID.SplinterBrick3,
    ResourceID.SplinterBrick4,
    ResourceID.SplinterBrick5,
  ];

  private static readonly COLUMN_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterColumn1,
    ResourceID.SplinterColumn2,
    ResourceID.SplinterColumn3,
    ResourceID.SplinterColumn4,
    ResourceID.SplinterColumn5,
  ];

  private static readonly GREEN_BOX_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterGreenBox1,
    ResourceID.SplinterGreenBox2,
    ResourceID.SplinterGreenBox3,
    ResourceID.SplinterGreenBox4,
    ResourceID.SplinterGreenBox5,
  ];

  private static readonly PURPLE_BOX_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPurpleBox1,
    ResourceID.SplinterPurpleBox2,
    ResourceID.SplinterPurpleBox3,
    ResourceID.SplinterPurpleBox4,
    ResourceID.SplinterPurpleBox5,
  ];

  private static readonly PLASTIC_BLUE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticBlue1,
    ResourceID.SplinterPlasticBlue2,
    ResourceID.SplinterPlasticBlue3,
    ResourceID.SplinterPlasticBlue4,
    ResourceID.SplinterPlasticBlue5,
  ];

  private static readonly PLASTIC_GREEN_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticGreen1,
    ResourceID.SplinterPlasticGreen2,
    ResourceID.SplinterPlasticGreen3,
    ResourceID.SplinterPlasticGreen4,
    ResourceID.SplinterPlasticGreen5,
  ];

  private static readonly PLASTIC_LIGHT_BLUE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticLightBlue1,
    ResourceID.SplinterPlasticLightBlue2,
    ResourceID.SplinterPlasticLightBlue3,
    ResourceID.SplinterPlasticLightBlue4,
    ResourceID.SplinterPlasticLightBlue5,
  ];

  private static readonly PLASTIC_ORANGE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticOrange1,
    ResourceID.SplinterPlasticOrange2,
    ResourceID.SplinterPlasticOrange3,
    ResourceID.SplinterPlasticOrange4,
    ResourceID.SplinterPlasticOrange5,
  ];

  private static readonly PLASTIC_PINK_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticPink1,
    ResourceID.SplinterPlasticPink2,
    ResourceID.SplinterPlasticPink3,
    ResourceID.SplinterPlasticPink4,
    ResourceID.SplinterPlasticPink5,
  ];

  private static readonly PLASTIC_PURPLE_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticPurple1,
    ResourceID.SplinterPlasticPurple2,
    ResourceID.SplinterPlasticPurple3,
    ResourceID.SplinterPlasticPurple4,
    ResourceID.SplinterPlasticPurple5,
  ];

  private static readonly PLASTIC_RED_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticRed1,
    ResourceID.SplinterPlasticRed2,
    ResourceID.SplinterPlasticRed3,
    ResourceID.SplinterPlasticRed4,
    ResourceID.SplinterPlasticRed5,
  ];

  private static readonly PLASTIC_YELLOW_SPLINTER_IDS: ResourceID[] = [
    ResourceID.SplinterPlasticYellow1,
    ResourceID.SplinterPlasticYellow2,
    ResourceID.SplinterPlasticYellow3,
    ResourceID.SplinterPlasticYellow4,
    ResourceID.SplinterPlasticYellow5,
  ];

  // --- Watermelon Splash Constants ---
  private static readonly WATERMELON_BURST_COUNT            : number      = 10;
  private static readonly WATERMELON_LIFESPAN               : number      = 0.8;
  private static readonly WATERMELON_LIFESPAN_MIN_MULT      : number      = 0.85;
  private static readonly WATERMELON_LIFESPAN_MAX_MULT      : number      = 1.15;
  private static readonly WATERMELON_RADIAL_MIN             : number      = 2;
  private static readonly WATERMELON_RADIAL_MAX             : number      = 6;
  private static readonly WATERMELON_UP_MIN                 : number      = 2;
  private static readonly WATERMELON_UP_MAX                 : number      = 6;
  private static readonly WATERMELON_GRAVITY                : number      = 14;
  private static readonly WATERMELON_MIN_SCALE              : number      = 0.45;
  private static readonly WATERMELON_MAX_SCALE              : number      = 0.65;
  private static readonly WATERMELON_SPAWN_DIST_MIN         : number      = 0.0;
  private static readonly WATERMELON_SPAWN_DIST_MAX         : number      = 0.15;
  private static readonly WATERMELON_ANGLE_JITTER           : number      = 0.2;
  private static readonly WATERMELON_OFFSET_Z_MIN           : number      = 0.05;
  private static readonly WATERMELON_OFFSET_Z_MAX           : number      = 0.15;
  private static readonly WATERMELON_INITIAL_DISSOLVE_MIN   : number      = 0.3;
  private static readonly WATERMELON_INITIAL_DISSOLVE_MAX   : number      = 0.4;
  private static readonly WATERMELON_TEXTURE_RES_ID         : ResourceID  = ResourceID.WatermelonJuiceTexture;

  private static sparks_gradient_id: number = -1;

  public is_active       : boolean = true;
  private position       : THREE.Vector3;
  private material       : BlockImpactMaterial;
  private start_time     : number;
  private has_spawned    : boolean = false;
  private total_lifespan : number;

  public constructor(
    position: THREE.Vector3,
    material: BlockImpactMaterial = BlockImpactMaterial.STONE
  )
  {
    this.position   = position.clone();
    this.material   = material;
    this.start_time = Vfx3DRuntime.current_time;

    switch ( this.material )
    {
      case BlockImpactMaterial.STONE:
      case BlockImpactMaterial.BRICK:
      case BlockImpactMaterial.COLUMN:
        this.total_lifespan = Math.max( VfxBallBlockImpactTask.SMOKE_LIFESPAN, VfxBallBlockImpactTask.SPLINTERS_LIFESPAN );
        break;
      case BlockImpactMaterial.WOOD:
      case BlockImpactMaterial.GREEN_BOX:
      case BlockImpactMaterial.PURPLE_BOX:
      case BlockImpactMaterial.PLASTIC_BLUE:
      case BlockImpactMaterial.PLASTIC_GREEN:
      case BlockImpactMaterial.PLASTIC_LIGHT_BLUE:
      case BlockImpactMaterial.PLASTIC_ORANGE:
      case BlockImpactMaterial.PLASTIC_PINK:
      case BlockImpactMaterial.PLASTIC_PURPLE:
      case BlockImpactMaterial.PLASTIC_RED:
      case BlockImpactMaterial.PLASTIC_YELLOW:
        this.total_lifespan = Math.max( VfxBallBlockImpactTask.SPLINTERS_LIFESPAN, VfxBallBlockImpactTask.WAVES_LIFESPAN );
        break;
      case BlockImpactMaterial.WATERMELON:
        this.total_lifespan = VfxBallBlockImpactTask.WATERMELON_LIFESPAN;
        break;
      case BlockImpactMaterial.METAL:
        this.total_lifespan = Math.max( VfxBallBlockImpactTask.SPARKS_LIFESPAN, VfxBallBlockImpactTask.WAVES_LIFESPAN );
        break;
      case BlockImpactMaterial.ICE:
      case BlockImpactMaterial.PLASTIC:
        this.total_lifespan = VfxBallBlockImpactTask.WAVES_LIFESPAN;
        break;
      default:
        this.total_lifespan = 0.55;
        break;
    }
  }

  public setPosition( pos: THREE.Vector3 ): void
  {
    this.position.copy( pos );
  }

  public stop( _behavior?: StopBehavior ): void
  {
    this.is_active = false;
  }

  public start(): void
  {
    this.is_active = true;
  }

  public accelerate( _factor: number ): void {}

  public onUpdate( now: number ): boolean
  {
    if ( !this.is_active )
      return true;

    if ( now < this.start_time )
      return false;

    const age = now - this.start_time;

    if ( !this.has_spawned )
    {
      this.has_spawned = true;
      switch ( this.material )
      {
        case BlockImpactMaterial.STONE:
          this.spawnSmoke();
          this.spawnSplinters( VfxBallBlockImpactTask.STONE_SPLINTER_IDS );
          break;
        case BlockImpactMaterial.BRICK:
          this.spawnSmoke();
          this.spawnSplinters( VfxBallBlockImpactTask.BRICK_SPLINTER_IDS );
          break;
        case BlockImpactMaterial.COLUMN:
          this.spawnSmoke();
          this.spawnSplinters( VfxBallBlockImpactTask.COLUMN_SPLINTER_IDS );
          break;
        case BlockImpactMaterial.WOOD:
          this.spawnSplinters( VfxBallBlockImpactTask.WOOD_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.GREEN_BOX:
          this.spawnSplinters( VfxBallBlockImpactTask.GREEN_BOX_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PURPLE_BOX:
          this.spawnSplinters( VfxBallBlockImpactTask.PURPLE_BOX_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_BLUE:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_BLUE_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_GREEN:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_GREEN_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_LIGHT_BLUE:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_LIGHT_BLUE_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_ORANGE:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_ORANGE_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_PINK:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_PINK_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_PURPLE:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_PURPLE_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_RED:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_RED_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.PLASTIC_YELLOW:
          this.spawnSplinters( VfxBallBlockImpactTask.PLASTIC_YELLOW_SPLINTER_IDS );
          this.spawnWaves();
          break;
        case BlockImpactMaterial.WATERMELON:
          this.spawnWatermelonSplash();
          break;
        case BlockImpactMaterial.METAL:
          this.spawnSparks();
          this.spawnWaves();
          break;
        case BlockImpactMaterial.ICE:
        case BlockImpactMaterial.PLASTIC:
          this.spawnWaves();
          break;
      }
    }

    if ( age >= this.total_lifespan )
    {
      this.stop();
      return true;
    }

    return false;
  }

  private static readonly scratch_pos: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_vel: THREE.Vector3 = new THREE.Vector3();
  private static readonly scratch_size: THREE.Vector2 = new THREE.Vector2();

  private spawnSparks(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxBallisticStreakBatch );

    if ( VfxBallBlockImpactTask.sparks_gradient_id === -1 )
    {
      VfxBallBlockImpactTask.sparks_gradient_id = VfxGradientAtlas.registerGradient([
        { stop: 0.0, color: VfxBallBlockImpactTask.SPARKS_COLOR_1 },
        { stop: VfxBallBlockImpactTask.SPARKS_COLOR_2_PROGRESS, color: VfxBallBlockImpactTask.SPARKS_COLOR_2 },
        { stop: VfxBallBlockImpactTask.SPARKS_COLOR_3_PROGRESS, color: VfxBallBlockImpactTask.SPARKS_COLOR_3 },
        { stop: 1.0, color: new THREE.Color( 0x000000 ) }
      ]);
    }

    for ( let i = 0; i < VfxBallBlockImpactTask.SPARKS_BURST_COUNT; i++ )
    {
      const angle = ( i / VfxBallBlockImpactTask.SPARKS_BURST_COUNT ) * Math.PI * 2 + randomRange( -0.2, 0.2 );
      const spawn_dist = randomRange( 0, VfxBallBlockImpactTask.SPARKS_SPAWN_AREA_RADIUS );
      VfxBallBlockImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + 0.01
      );

      const speed = randomRange(
        VfxBallBlockImpactTask.SPARKS_MIN_SPEED,
        VfxBallBlockImpactTask.SPARKS_MAX_SPEED
      );
      const up_speed = randomRange(
        VfxBallBlockImpactTask.SPARKS_UP_MIN,
        VfxBallBlockImpactTask.SPARKS_UP_MAX
      );
      VfxBallBlockImpactTask.scratch_vel.set(
        Math.cos( angle ) * speed,
        Math.sin( angle ) * speed + up_speed,
        0.0
      );

      const width = randomRange(
        VfxBallBlockImpactTask.SPARKS_MIN_WIDTH,
        VfxBallBlockImpactTask.SPARKS_MAX_WIDTH
      );
      const height = randomRange(
        VfxBallBlockImpactTask.SPARKS_MIN_HEIGHT,
        VfxBallBlockImpactTask.SPARKS_MAX_HEIGHT
      );
      VfxBallBlockImpactTask.scratch_size.set( width, height );

      batch.spawnParticle(
        ResourceID.EllipseGlow1,
        VfxBallBlockImpactTask.sparks_gradient_id,
        VfxBallBlockImpactTask.scratch_pos,
        VfxBallBlockImpactTask.scratch_vel,
        VfxBallBlockImpactTask.SPARKS_GRAVITY,
        VfxBallBlockImpactTask.scratch_size,
        VfxBallBlockImpactTask.SPARKS_STRETCH_FACTOR,
        VfxBallBlockImpactTask.SPARKS_SHRINK_START_RATIO,
        this.start_time,
        VfxBallBlockImpactTask.SPARKS_LIFESPAN
      );
    }
  }

  private spawnSmoke(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxImpactSmokeBatch );

    for ( let i = 0; i < VfxBallBlockImpactTask.SMOKE_BURST_COUNT; i++ )
    {
      const angle = ( i / VfxBallBlockImpactTask.SMOKE_BURST_COUNT ) * Math.PI * 2 + randomRange( -0.15, 0.15 );
      const spawn_dist = randomRange( 0, 0.2 );
      VfxBallBlockImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + randomRange( -0.05, 0.05 )
      );

      const flight_distance = randomRange(
        VfxBallBlockImpactTask.SMOKE_MIN_FLIGHT_DIST,
        VfxBallBlockImpactTask.SMOKE_MAX_FLIGHT_DIST
      );
      const speed = flight_distance / VfxBallBlockImpactTask.SMOKE_LIFESPAN;

      VfxBallBlockImpactTask.scratch_vel.set(
        Math.cos( angle ) * speed,
        Math.sin( angle ) * speed,
        randomRange( -0.1, 0.1 ) * speed
      );

      const start_scale = randomRange(
        VfxBallBlockImpactTask.SMOKE_MIN_START_SCALE,
        VfxBallBlockImpactTask.SMOKE_MAX_START_SCALE
      );
      const end_scale = randomRange(
        VfxBallBlockImpactTask.SMOKE_MIN_END_SCALE,
        VfxBallBlockImpactTask.SMOKE_MAX_END_SCALE
      );
      const initial_angle = randomRange( 0, Math.PI * 2 );
      const rotation_speed = randomRange( -1.0, 1.0 );

      batch.spawnParticle(
        VfxBallBlockImpactTask.scratch_pos,
        VfxBallBlockImpactTask.scratch_vel,
        start_scale,
        end_scale,
        VfxBallBlockImpactTask.SMOKE_COLOR,
        VfxBallBlockImpactTask.SMOKE_INITIAL_ALPHA,
        initial_angle,
        rotation_speed,
        this.start_time,
        VfxBallBlockImpactTask.SMOKE_LIFESPAN
      );
    }
  }

  private spawnWaves(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxPlanarWaveBatch );
    const count = VfxBallBlockImpactTask.WAVES_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( ( i + 0.5 ) / count ) * Math.PI * 2.0 + randomRange( -0.1, 0.1 );
      const spawn_dist = randomRange(
        VfxBallBlockImpactTask.WAVES_MIN_SPAWN_AREA_RADIUS,
        VfxBallBlockImpactTask.WAVES_MAX_SPAWN_AREA_RADIUS
      );

      VfxBallBlockImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + 0.05
      );

      VfxBallBlockImpactTask.scratch_vel.set(
        Math.cos( angle ) * VfxBallBlockImpactTask.WAVES_SPEED,
        Math.sin( angle ) * VfxBallBlockImpactTask.WAVES_SPEED,
        0.0
      );

      const width  = randomRange( VfxBallBlockImpactTask.WAVES_MIN_WIDTH, VfxBallBlockImpactTask.WAVES_MAX_WIDTH );
      const height = randomRange( VfxBallBlockImpactTask.WAVES_MIN_HEIGHT, VfxBallBlockImpactTask.WAVES_MAX_HEIGHT );

      const res_id = Math.random() < 0.5 ? ResourceID.ShockwaveGlow2 : ResourceID.ShockwaveGlow3;

      batch.spawnParticle(
        VfxBallBlockImpactTask.scratch_pos,
        VfxBallBlockImpactTask.scratch_vel,
        width,
        height,
        VfxBallBlockImpactTask.WAVES_FADE_START_PROGRESS,
        this.start_time,
        VfxBallBlockImpactTask.WAVES_LIFESPAN,
        VfxBallBlockImpactTask.WAVES_MAX_ALPHA,
        VfxBallBlockImpactTask.WAVES_COLOR,
        res_id
      );
    }
  }

  private spawnSplinters( splinter_ids: ResourceID[] ): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxShardBatch );

    for ( let i = 0; i < VfxBallBlockImpactTask.SPLINTERS_BURST_COUNT; i++ )
    {
      const angle = ( i / VfxBallBlockImpactTask.SPLINTERS_BURST_COUNT ) * Math.PI * 2 + randomRange( -0.2, 0.2 );
      const spawn_dist = randomRange( 0, 0.15 );
      VfxBallBlockImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + randomRange( 0.05, 0.15 )
      );

      const speed = randomRange(
        VfxBallBlockImpactTask.SPLINTERS_MIN_SPEED,
        VfxBallBlockImpactTask.SPLINTERS_MAX_SPEED
      );
      const elevation = randomRange( 0.2, 0.6 );
      VfxBallBlockImpactTask.scratch_vel.set(
        Math.cos( angle ) * speed,
        ( Math.sin( angle ) * 0.4 + elevation ) * speed,
        randomRange( 0.2, 0.7 ) * speed
      );

      const scale = randomRange(
        VfxBallBlockImpactTask.SPLINTERS_MIN_SCALE,
        VfxBallBlockImpactTask.SPLINTERS_MAX_SCALE
      );

      const res_id = splinter_ids[ Math.floor( Math.random() * splinter_ids.length ) ];
      const spin_dir = VfxBallBlockImpactTask.scratch_vel.x >= 0 ? 1 : -1;
      const spin_speed = randomRange( 4.0, 10.0 );

      batch.spawnShard(
        res_id,
        VfxBallBlockImpactTask.scratch_pos,
        VfxBallBlockImpactTask.scratch_vel,
        VfxBallBlockImpactTask.SPLINTERS_GRAVITY,
        scale,
        randomRange( 0, Math.PI * 2 ),
        spin_dir * spin_speed,
        this.start_time,
        VfxBallBlockImpactTask.SPLINTERS_LIFESPAN * randomRange( 0.8, 1.0 ),
        0.0,
        VfxBallBlockImpactTask.SPLINTERS_FADE_START,
        VfxBallBlockImpactTask.SPLINTERS_SHRINK_START,
        0.0,
        0.25
      );
    }
  }

  private spawnWatermelonSplash(): void
  {
    const batch = Vfx3DRuntime.getBatch( VfxSplashBatch );
    const count = VfxBallBlockImpactTask.WATERMELON_BURST_COUNT;

    for ( let i = 0; i < count; i++ )
    {
      const angle = ( i / count ) * Math.PI * 2 + randomRange(
        -VfxBallBlockImpactTask.WATERMELON_ANGLE_JITTER,
        VfxBallBlockImpactTask.WATERMELON_ANGLE_JITTER
      );
      const spawn_dist = randomRange(
        VfxBallBlockImpactTask.WATERMELON_SPAWN_DIST_MIN,
        VfxBallBlockImpactTask.WATERMELON_SPAWN_DIST_MAX
      );
      VfxBallBlockImpactTask.scratch_pos.set(
        this.position.x + Math.cos( angle ) * spawn_dist,
        this.position.y + Math.sin( angle ) * spawn_dist,
        this.position.z + randomRange(
          VfxBallBlockImpactTask.WATERMELON_OFFSET_Z_MIN,
          VfxBallBlockImpactTask.WATERMELON_OFFSET_Z_MAX
        )
      );

      const rs = randomRange(
        VfxBallBlockImpactTask.WATERMELON_RADIAL_MIN,
        VfxBallBlockImpactTask.WATERMELON_RADIAL_MAX
      );
      const up_speed = randomRange(
        VfxBallBlockImpactTask.WATERMELON_UP_MIN,
        VfxBallBlockImpactTask.WATERMELON_UP_MAX
      );
      VfxBallBlockImpactTask.scratch_vel.set(
        Math.cos( angle ) * rs,
        up_speed,
        Math.sin( angle ) * rs
      );

      const scale = randomRange(
        VfxBallBlockImpactTask.WATERMELON_MIN_SCALE,
        VfxBallBlockImpactTask.WATERMELON_MAX_SCALE
      );

      const initial_dissolve = randomRange(
        VfxBallBlockImpactTask.WATERMELON_INITIAL_DISSOLVE_MIN,
        VfxBallBlockImpactTask.WATERMELON_INITIAL_DISSOLVE_MAX
      );

        batch.spawnParticle(
          VfxBallBlockImpactTask.scratch_pos,
          VfxBallBlockImpactTask.scratch_vel,
          VfxBallBlockImpactTask.WATERMELON_GRAVITY,
          scale,
          this.start_time,
          VfxBallBlockImpactTask.WATERMELON_LIFESPAN * randomRange(
            VfxBallBlockImpactTask.WATERMELON_LIFESPAN_MIN_MULT,
            VfxBallBlockImpactTask.WATERMELON_LIFESPAN_MAX_MULT
          ),
          VfxBallBlockImpactTask.WATERMELON_TEXTURE_RES_ID,
          initial_dissolve
        );
    }
  }
}
